import { Component, Input, ElementRef, ViewChild, AfterViewInit, OnDestroy, signal, computed, effect, inject, Injector, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConfigService } from '../../../services/config.service';

export type ScrollbarVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'accent';
export type ScrollbarSize = 'thin' | 'medium' | 'thick';
export type ScrollbarVisibility = 'always' | 'hover' | 'auto';

@Component({
  selector: 'app-scrollbar',
  imports: [CommonModule],
  templateUrl: './scrollbar.component.html',
  styles: []
})
export class ScrollbarComponent implements AfterViewInit, OnDestroy {
  @Input() variant: ScrollbarVariant = 'primary';
  @Input() size: ScrollbarSize = 'medium';
  @Input() visibility: ScrollbarVisibility = 'auto';
  @Input() showScrollButtons: boolean = false;
  @Input() showScrollIndicator: boolean = false;
  @Input() smoothScroll: boolean = true;
  @Input() autoHide: boolean = true;
  @Input() maxHeight: string = '100%';
  @Input() maxWidth: string = '100%';
  @Input() class: string = '';

  @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef<HTMLElement>;

  showScrollTop = signal(false);
  showScrollBottom = signal(false);
  showScrollLeft = signal(false);
  showScrollRight = signal(false);
  scrollPosition = signal({ x: 0, y: 0 });
  scrollPercentage = signal({ x: 0, y: 0 });
  isHovered = signal(false);
  themeUpdateTrigger = signal(0); // Trigger for theme updates

  private scrollListener?: () => void;
  private resizeObserver?: ResizeObserver;

  private configService = inject(ConfigService, { optional: true });

  constructor() {
    // Watch for theme changes if ConfigService is available
    if (this.configService) {
      effect(() => {
        // Trigger update when theme or mode changes
        this.configService?.mode();
        this.configService?.config();
        this.themeUpdateTrigger.update(v => v + 1);
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.scrollContainer) {
      this.setupScrollListener();
      this.setupResizeObserver();
      this.updateScrollIndicators();
    }
  }

  ngOnDestroy(): void {
    if (this.scrollListener) {
      this.scrollContainer?.nativeElement.removeEventListener('scroll', this.scrollListener);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private setupScrollListener(): void {
    this.scrollListener = () => {
      this.updateScrollIndicators();
      this.updateScrollPosition();
    };
    this.scrollContainer.nativeElement.addEventListener('scroll', this.scrollListener, { passive: true });
  }

  private setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.updateScrollIndicators();
    });
    this.resizeObserver.observe(this.scrollContainer.nativeElement);
  }

  private updateScrollIndicators(): void {
    if (!this.scrollContainer) return;

    const element = this.scrollContainer.nativeElement;
    const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } = element;

    this.showScrollTop.set(scrollTop > 10);
    this.showScrollBottom.set(scrollTop < scrollHeight - clientHeight - 10);
    this.showScrollLeft.set(scrollLeft > 10);
    this.showScrollRight.set(scrollLeft < scrollWidth - clientWidth - 10);
  }

  private updateScrollPosition(): void {
    if (!this.scrollContainer) return;

    const element = this.scrollContainer.nativeElement;
    const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } = element;

    const x = scrollLeft;
    const y = scrollTop;
    const xPercent = scrollWidth > clientWidth ? (scrollLeft / (scrollWidth - clientWidth)) * 100 : 0;
    const yPercent = scrollHeight > clientHeight ? (scrollTop / (scrollHeight - clientHeight)) * 100 : 0;

    this.scrollPosition.set({ x, y });
    this.scrollPercentage.set({ x: xPercent, y: yPercent });
  }

  scrollToTop(): void {
    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollTo({
        top: 0,
        behavior: this.smoothScroll ? 'smooth' : 'auto'
      });
    }
  }

  scrollToBottom(): void {
    if (this.scrollContainer) {
      const element = this.scrollContainer.nativeElement;
      element.scrollTo({
        top: element.scrollHeight,
        behavior: this.smoothScroll ? 'smooth' : 'auto'
      });
    }
  }

  scrollToLeft(): void {
    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollTo({
        left: 0,
        behavior: this.smoothScroll ? 'smooth' : 'auto'
      });
    }
  }

  scrollToRight(): void {
    if (this.scrollContainer) {
      const element = this.scrollContainer.nativeElement;
      element.scrollTo({
        left: element.scrollWidth,
        behavior: this.smoothScroll ? 'smooth' : 'auto'
      });
    }
  }

  scrollTo(x: number, y: number): void {
    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollTo({
        left: x,
        top: y,
        behavior: this.smoothScroll ? 'smooth' : 'auto'
      });
    }
  }

  onMouseEnter(): void {
    this.isHovered.set(true);
  }

  onMouseLeave(): void {
    this.isHovered.set(false);
  }

  get containerClasses(): string {
    const classes = [
      // Base styles
      'overflow-auto'
    ];

    if (this.smoothScroll) {
      classes.push('scroll-smooth');
    }

    if (this.class) {
      classes.push(this.class);
    }

    return classes.join(' ');
  }

  get scrollbarStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    
    // Firefox scrollbar-width
    switch (this.size) {
      case 'thin':
        styles['scrollbar-width'] = 'thin';
        break;
      case 'medium':
        styles['scrollbar-width'] = 'thin';
        break;
      case 'thick':
        styles['scrollbar-width'] = 'auto';
        break;
    }

    // Firefox scrollbar-color - use theme colors
    const root = getComputedStyle(document.documentElement);
    const trackColor = root.getPropertyValue('--card-background').trim() || 
                      (document.documentElement.classList.contains('dark') ? 'rgb(31 41 55)' : 'rgb(243 244 246)');
    
    const thumbColor = this.getThemeColor(this.variant, 'base');
    styles['scrollbar-color'] = `${thumbColor} ${trackColor}`;

    return styles;
  }

  get webkitScrollbarWidth(): string {
    switch (this.size) {
      case 'thin':
        return '4px';
      case 'medium':
        return '8px';
      case 'thick':
        return '12px';
      default:
        return '8px';
    }
  }

  private getThemeColor(variant: ScrollbarVariant, type: 'base' | 'hover' | 'active'): string {
    const root = getComputedStyle(document.documentElement);
    
    // Map variants to CSS variables
    const variantMap: { [key: string]: { base: string; hover: string; active: string } } = {
      primary: {
        base: root.getPropertyValue('--theme-color-1').trim() || root.getPropertyValue('--primary-color').trim() || '#2563eb',
        hover: this.darkenColor(root.getPropertyValue('--theme-color-1').trim() || root.getPropertyValue('--primary-color').trim() || '#2563eb', 10),
        active: this.darkenColor(root.getPropertyValue('--theme-color-1').trim() || root.getPropertyValue('--primary-color').trim() || '#2563eb', 20)
      },
      secondary: {
        base: root.getPropertyValue('--theme-color-2').trim() || root.getPropertyValue('--secondary-color').trim() || '#9ca3af',
        hover: this.darkenColor(root.getPropertyValue('--theme-color-2').trim() || root.getPropertyValue('--secondary-color').trim() || '#9ca3af', 10),
        active: this.darkenColor(root.getPropertyValue('--theme-color-2').trim() || root.getPropertyValue('--secondary-color').trim() || '#9ca3af', 20)
      },
      success: {
        base: root.getPropertyValue('--theme-color-4').trim() || root.getPropertyValue('--success-color').trim() || '#22c55e',
        hover: this.darkenColor(root.getPropertyValue('--theme-color-4').trim() || root.getPropertyValue('--success-color').trim() || '#22c55e', 10),
        active: this.darkenColor(root.getPropertyValue('--theme-color-4').trim() || root.getPropertyValue('--success-color').trim() || '#22c55e', 20)
      },
      danger: {
        base: '#ef4444',
        hover: '#dc2626',
        active: '#b91c1c'
      },
      warning: {
        base: root.getPropertyValue('--theme-color-5').trim() || root.getPropertyValue('--warning-color').trim() || '#f59e0b',
        hover: this.darkenColor(root.getPropertyValue('--theme-color-5').trim() || root.getPropertyValue('--warning-color').trim() || '#f59e0b', 10),
        active: this.darkenColor(root.getPropertyValue('--theme-color-5').trim() || root.getPropertyValue('--warning-color').trim() || '#f59e0b', 20)
      },
      accent: {
        base: root.getPropertyValue('--theme-color-3').trim() || root.getPropertyValue('--accent-color').trim() || '#a855f7',
        hover: this.darkenColor(root.getPropertyValue('--theme-color-3').trim() || root.getPropertyValue('--accent-color').trim() || '#a855f7', 10),
        active: this.darkenColor(root.getPropertyValue('--theme-color-3').trim() || root.getPropertyValue('--accent-color').trim() || '#a855f7', 20)
      }
    };

    const variantColors = variantMap[variant] || variantMap['primary'];
    return variantColors[type];
  }

  private darkenColor(color: string, percent: number): string {
    if (!color || color === '') return '#2563eb';
    
    // Handle hex colors
    if (color.startsWith('#')) {
      const num = parseInt(color.replace('#', ''), 16);
      const r = Math.max(0, Math.floor((num >> 16) * (1 - percent / 100)));
      const g = Math.max(0, Math.floor(((num >> 8) & 0x00FF) * (1 - percent / 100)));
      const b = Math.max(0, Math.floor((num & 0x0000FF) * (1 - percent / 100)));
      return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
    }
    
    // Handle rgb/rgba colors
    if (color.startsWith('rgb')) {
      const matches = color.match(/\d+/g);
      if (matches && matches.length >= 3) {
        const r = Math.max(0, Math.floor(parseInt(matches[0]) * (1 - percent / 100)));
        const g = Math.max(0, Math.floor(parseInt(matches[1]) * (1 - percent / 100)));
        const b = Math.max(0, Math.floor(parseInt(matches[2]) * (1 - percent / 100)));
        return `rgb(${r}, ${g}, ${b})`;
      }
    }
    
    return color;
  }

  get webkitScrollbarThumbColor(): string {
    return this.getThemeColor(this.variant, 'base');
  }

  get webkitScrollbarThumbHoverColor(): string {
    return this.getThemeColor(this.variant, 'hover');
  }

  get webkitScrollbarThumbActiveColor(): string {
    return this.getThemeColor(this.variant, 'active');
  }

  get scrollbarStyleSheet(): string {
    const className = `scrollbar-container-${this.variant}-${this.size}-${this.visibility}`;
    const displayRule = (this.visibility === 'hover' || this.visibility === 'auto') ? 'display: none;' : '';
    const hoverDisplayRule = (this.visibility === 'hover' || this.visibility === 'auto') ? 'display: block;' : '';
    
    // Get theme colors dynamically
    const root = getComputedStyle(document.documentElement);
    const trackColor = root.getPropertyValue('--card-background').trim() || 
                      (document.documentElement.classList.contains('dark') ? 'rgb(31 41 55)' : 'rgb(243 244 246)');
    const darkTrackColor = root.getPropertyValue('--card-background').trim() || 'rgb(31 41 55)';
    
    return `
      .${className}::-webkit-scrollbar {
        width: ${this.webkitScrollbarWidth};
        height: ${this.webkitScrollbarWidth};
        ${displayRule}
      }
      
      .${className}:hover::-webkit-scrollbar,
      .${className}:focus::-webkit-scrollbar {
        ${hoverDisplayRule}
      }
      
      .${className}::-webkit-scrollbar-track {
        background-color: ${trackColor};
        border-radius: 0.5rem;
      }
      
      .dark .${className}::-webkit-scrollbar-track {
        background-color: ${darkTrackColor};
      }
      
      .${className}::-webkit-scrollbar-thumb {
        background-color: ${this.webkitScrollbarThumbColor};
        border-radius: 0.5rem;
        transition: background-color 0.2s ease;
      }
      
      .${className}::-webkit-scrollbar-thumb:hover {
        background-color: ${this.webkitScrollbarThumbHoverColor};
      }
      
      .${className}::-webkit-scrollbar-thumb:active {
        background-color: ${this.webkitScrollbarThumbActiveColor};
      }
    `;
  }
}

